import React, { useEffect, useMemo, useRef, useState } from "react";
import { subscribeApplicantEvents } from "@service/applicant-events";
import { Input } from "@component/input";
import { Button } from "@component/button";
import {
    SearchIcon,
    ChevronDown,
    UserPlusIcon,
    ArrowUpDownIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    XIcon,
    ClockIcon,
    TimerIcon,
    FileIcon,
    ClipboardListIcon,
    AlertTriangleIcon,
    SearchCheckIcon,
    BriefcaseIcon,
    XCircleIcon,
    UploadIcon,
    ChartNoAxesColumn,
} from "lucide-react";
import { Job, ReviewScheduledNoteItem } from "@type/job";
import {
    Applicant,
    ApplicantFunnelCount,
    ApplicantStatusCount,
    MatchLevelItem,
} from "@type/applicant";
import { defaultPagination, matchLevelApplicantOptions } from "@constant/app";
import { RejectionReasonDialog } from "@component/reject-offer-reason-dialog";
import {
    applicantUpdateStatus,
    calculateSimiliarityScoreBatch,
    deleteApplicants,
    getApplicantCount,
    getApplicantsScores,
    getJobApplicantGroup,
    getJobApplicants,
    restoreApplicants,
} from "@service/job";
import { Link, useNavigate } from "react-router";
import { MetaPagination } from "@type/common";
import { getShortlistedCandidates, getShortlistedByCandidateAndJobID } from "@service/user";
import { setCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import { ApplicantInsights } from "./applicant-insight";
import { catchErrorLog } from "@util/sentry";
import { Badge } from "@component/badge";
import { cn } from "@util/tailwind";
import {
    convertCapitalizeWords,
    convertToLowerSnakeCase,
    getApplicantStatusBadge,
    getFunnelStatus,
} from "@util/string";
import { useSnackbar } from "@hook/use-snackbar";
import { SendCustomizedEmailRequest } from "@type/candidate";
import { sendCustomizedEmailV2 } from "@service/employer";
import { useSubscription } from "@context/subscription_context";
import { useMaintenanceGate } from "@context/maintenance";
import { CreateContractRequest } from "@type/contract";
import { createContractV2 } from "@service/contract";
import { Tooltip } from "@component/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import DrawerCandidateProfile from "./drawer/drawer-candidate-profile";
import Pagination from "@component/pagination";
import BulkApplicationActionsBar from "./bulk-applicantion-action";
import InterviewInvitationDialogV2 from "@component/interview-invitation-v2-dialog";
import { useBulkUploadCVContext } from "@context/bulk_upload_cv_context";
import RejectCandidateDialogV2 from "@component/job-reject-v2-dialog";
import RejectCandidateWhatsappDialog from "@component/job-reject-whatsapp-dialog";
import WhatsappScreeningInvitationDialog from "@container/dialog/dialog-whatsapp-screening-invitation";
import JobOfferDialogV2 from "@component/job-offer-v2-dialog";
import AssessmentInvitationDialogV2 from "@component/assessment-invitation-v2-dialog";
import AssessmentWarningDialog from "./dialog/dialog-assessment-warning";
import ReviewScheduleManagerDialog from "@component/review-schedule-manager-dialog";
import InternalOfferNoteDialog from "./dialog/dialog-internal-offer-note";
import BulkCVUploadDialog from "./dialog/dialog-bulk-cv";
import TableListApplicant from "./table-list-applicant";
import TableListUpload from "./table-list-uploads";
import RestoreCandidatesValidationDialog from "./dialog/dialog-restore-candidate";
import DeleteCandidateDialog, { isDeleteConfirmSuppressed } from "./dialog/dialog-delete-candidate";

interface Props {
    job: Job | null;
}

const LIMIT = 20;

export function ApplicantsList({ job }: Props) {
    const snackbar = useSnackbar();
    const { showTrialUsageLimitDialog, isJobActionBlocked } = useSubscription();
    // `isJobActionBlocked` already folds maintenance in, so Upload Bulk CVs is
    // covered. This is for the controls that are not subscription-gated.
    const maintenance = useMaintenanceGate();
    // Bulk CV upload is blocked once the workspace is restricted (trial expired
    // + past the 7-day grace) or the trial CV-screening quota is exhausted —
    // see subscription_context `isJobActionBlocked`.
    const isSubscriptionLimitReached = isJobActionBlocked;
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const { isProcessing, reloadCount, setIsVisible } = useBulkUploadCVContext();
    const [, currentUserInfo] = useUserProfileContext();
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setLoading] = useState<boolean>(true);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [isMarkOfferResent, setIsMarkOfferResent] = useState<boolean>(false);
    const [sortField, setSortField] = useState<string>("similarity_score");
    const [sortDirection, setSortDirection] = useState<string>("desc");
    const [similarityScoreCandidate, setSimilarityScoreCandidate] = useState<Record<string, number>>({});
    const [funnelCounts, setFunnelCounts] = useState<ApplicantFunnelCount>({
        total_applicant: 0,
        uploads: 0,
        applied: 0,
        pending_assessment: 0,
        to_review: 0,
        offers: 0,
        closed: 0,
        show_upload_tab: 0,
    })
    const [drawerActiveTab, setDrawerActiveTab] = useState<string>("profile");
    const [focusComposerOnOpen, setFocusComposerOnOpen] = useState<boolean>(false);
    const [activeFunnelStage, setActiveFunnelStage] = useState<string>("applied");
    const [matchFilter, setMatchFilter] = useState<MatchLevelItem>({
        label: "All Match Levels",
        value: "all"
    });
    const [applicants, setApplicants] = useState<Applicant[]>([]);
    const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(
        null,
    );
    const [showAssessmentInvitationDialog, setShowAssessmentInvitationDialog] = useState<boolean>(false);
    const [showAssessmentResendDialog, setShowAssessmentResendDialog] = useState<boolean>(false);
    const [showAssessmentInvitationReminderDialog, setShowAssessmentInvitationReminderDialog] = useState<boolean>(false);
    const [showAssessmentRetakeDialog, setShowAssessmentRetakeDialog] = useState<boolean>(false);
    const [isBulkCVUploadDialogOpen, setIsBulkCVUploadDialogOpen] = useState<boolean>(false);
    const [isRestoreValidationOpen, setIsRestoreValidationOpen] = useState<boolean>(false);
    const [isReviewScheduleManagerDialogOpen, setIsReviewScheduleManagerDialogOpen] = useState<boolean>(false);
    const [isInternalOfferNoteDialogOpen, setIsInternalOfferNoteDialogOpen] = useState<boolean>(false);
    const [showInterviewInvitationDialog, setShowInterviewInvitationDialog] = useState<boolean>(false);
    const [isAssessmentWarningOpen, setIsAssessmentWarningOpen] = useState<boolean>(false);
    const [countIneligibleCandidates] = useState<number>(0);
    const [showRejectOfferDialog, setShowRejectOfferDialog] = useState<boolean>(false);
    // #3186/#3877: WhatsApp reject dialog for flagged candidates with no email.
    const [showWaRejectDialog, setShowWaRejectDialog] = useState<boolean>(false);
    // #3478: bulk screening invitation over WhatsApp (preview then send).
    const [showWaInvitationDialog, setShowWaInvitationDialog] = useState<boolean>(false);
    const [showJobOfferDialog, setShowJobOfferDialog] = useState<boolean>(false);
    const [isShowCandidateDrawer, setShowCandidateDrawer] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(defaultPagination.page);
    const [chipStatusFilter, setChipStatusFilter] = useState<string | null>(null);
    const [applicantGroupStatus, setApplicantGroupStatus] = useState<ApplicantStatusCount[]>([]);
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })
    const [shortlistedCountMap, setShortlistedCountMap] = useState<Record<string, number>>({});
    const [commentCountMap] = useState<Record<string, number>>({});
    const [isShowRejectReasonDialog, setShowRejectReasonDialog] =
        useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
    const [isDeletingCandidates, setIsDeletingCandidates] = useState<boolean>(false);
    const [deleteCandidateTargets, setDeleteCandidateTargets] = useState<Applicant[]>([]);

    // #3316 (BE ourteam-backend#4092): SSE-triggered silent refresh machinery.
    // `lastSearchRef` remembers the exact args of the last user-driven fetch so
    // a background refresh replays the current view (page/search/filters/sort
    // intact, no spinner). Pending flag queues a refresh that arrived while a
    // bulk selection was active, so it can't be clobbered.
    const lastSearchRef = useRef<{ page: number; keyword: string; application_status: string[]; matchLevel: string; isBadgeFilter: boolean; funnelStage: string } | null>(null);
    const sseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSseRefreshRef = useRef<boolean>(false);
    const silentSseRefreshRef = useRef<() => void>(() => undefined);

    const onSearch = async (page: number, keyword: string, application_status: string[], matchLevel: string, isBadgeFilter: boolean = false, funnelStage?: string, silent: boolean = false) => {
        if (!silent) {
            setLoading(true);
            lastSearchRef.current = { page, keyword, application_status: [...application_status], matchLevel, isBadgeFilter, funnelStage: funnelStage ?? activeFunnelStage };
        }
        let stage = funnelStage ?? activeFunnelStage;
        // Customizable Hiring Flow (#3186): the "Flagged" tab and the flow-jobs-only
        // Screening split are both the pending_assessment funnel filtered by the two
        // gate-hold statuses (Knockout Flagged · Deadline Missed). Map them to a real
        // funnel_status + status filter here so every existing reload callsite keeps
        // working unchanged.
        if (stage === "flagged") {
            application_status = FLOW_FLAG_STATUSES;
            isBadgeFilter = true;
            stage = "pending_assessment";
        } else if (stage === "pending_assessment" && hiringFlowEnabled && !isBadgeFilter) {
            // Flow jobs: Screening lists only "qualified" (non-flagged) candidates —
            // the flagged ones live in the dedicated Flagged tab. Gated on the
            // (synchronous) company flag so the filter applies on the first fetch;
            // non-flow jobs have no flag-status candidates, so this is a no-op there.
            application_status = getFunnelStatus("pending_assessment").filter((s) => !FLOW_FLAG_STATUSES.includes(s));
            isBadgeFilter = true;
        }
        getJobApplicants({
            job_id: job?.id || "",
            page: page,
            limit: LIMIT,
            keyword: keyword,
            application_status: isBadgeFilter && application_status.length > 0 ? application_status.join(",") : "",
            match_level: matchLevel !== "all" ? matchLevel : "",
            sort_by: sortField,
            sort: sortDirection,
            funnel_status: stage,
        }).then((resp) => {
            setApplicants(resp.data.data);
            setMetaPagination(resp.data.meta.pagination);

            // Shortlisted counts: the list returns them per row. Older backends don't,
            // so fall back to one call per row only then.
            const pageRows = resp.data.data as Applicant[];
            if (pageRows.length > 0 && pageRows.every((a) => typeof a.shortlisted_count === "number")) {
                setShortlistedCountMap(
                    Object.fromEntries(pageRows.map((a) => [a.applicant_id, a.shortlisted_count as number]))
                );
            } else if (job?.id && pageRows.length > 0) {
                const applicantsList = pageRows;
                Promise.all(
                    applicantsList.map((a) =>
                        getShortlistedByCandidateAndJobID(a.applicant_id, job.id)
                            .then((res) => ({
                                applicantId: a.applicant_id,
                                count: res.status === 200 ? (res.data.data || []).length : 0,
                            }))
                            .catch(() => ({ applicantId: a.applicant_id, count: 0 }))
                    )
                ).then((results) => {
                    const countMap: Record<string, number> = {};
                    results.forEach((r) => {
                        countMap[r.applicantId] = r.count;
                    });
                    setShortlistedCountMap(countMap);
                });

            }

            // Batch calculate similarity scores for unscored applicants
            if (job?.id) {
                const unscoredApplicants = (resp.data.data as Applicant[]).filter(
                    (a) => a.similarity_score === "-"
                );
                if (unscoredApplicants.length > 0) {
                    const candidateIds = unscoredApplicants.map((a) => a.applicant_id);
                    calculateSimiliarityScoreBatch(job.id, candidateIds)
                        .then((results) => {
                            if (results && results.length > 0) {
                                const scoresMap: Record<string, number> = {};
                                results.forEach((r) => {
                                    scoresMap[r.candidate_id] = r.similarity_score;
                                });
                                setSimilarityScoreCandidate((prev) => ({ ...prev, ...scoresMap }));
                                setApplicants((prev) =>
                                    prev.map((a) =>
                                        scoresMap[a.applicant_id] !== undefined
                                            ? { ...a, similarity_score: scoresMap[a.applicant_id] }
                                            : a
                                    )
                                );
                            }
                        })
                        .catch((err) => {
                            catchErrorLog(`Failed to batch calculate similarity scores: ${err}`);
                        });
                }
            }
        }).catch((err) => {
            catchErrorLog(`Failed to fetch applicants for job:`, err);
            setApplicants([]);
        }).finally(() => {
            setLoading(false);
        })

        // Refresh the status split even when a Screening/Flagged status filter was
        // injected above (isBadgeFilter true), so the tab badges stay correct.
        if (!isBadgeFilter || stage === "pending_assessment") {
            getJobApplicantGroup(job?.id || "", "", stage).then((resp) => {
                setApplicantGroupStatus(resp.data.data);
            })
        }
    }

    const onLoadPagination = (page: number) => {
        setCurrentPage(page);
        onSearch(page, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
    }

    const onFilterStatus = (status: string) => {
        if (status === "") {
            onSearch(1, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
        } else {
            onSearch(1, searchQuery, [status], matchFilter.value, true);
        }
    }

    const onLoadApplicationCount = (job_id: string) => {
        getApplicantCount(job_id).then((resp) => {
            if (resp.status === 200) {
                setFunnelCounts(resp.data.data);
            }
        })
    }

    // ---- Customizable Hiring Flow board surfaces (#3073/#3075) ----------------
    // Sticky detection: rows of custom-flow jobs carry flow metadata (BE #3640);
    // default jobs never do, so their board stays byte-identical to today.
    const [isFlowJob, setIsFlowJob] = useState<boolean>(false);
    // Per-company rollout flag (#3084): require an EXPLICIT true — absent field
    // (old BE / partial rollout) ⇒ OFF, matching the stated contract, so flow
    // UI never surfaces fail-open. (Detection stays sticky once true so columns
    // don't flicker away on a tab/page that happens to hold no flow rows.)
    const hiringFlowEnabled = (job as (Job & { company_hiring_flow_enabled?: boolean }) | null)?.company_hiring_flow_enabled === true;
    useEffect(() => {
        if (!hiringFlowEnabled) return;
        if (applicants.some((a) => !!a.flow_progress || !!a.current_step || !!a.review_kind)) {
            setIsFlowJob(true);
        }
    }, [applicants, hiringFlowEnabled]);

    // Qualified / Flagged sub-views — a FILTER on the two gate-hold statuses
    // (Knockout Flagged · Deadline Missed), not a stage (mockup P4/§4.3).
    const FLOW_FLAG_STATUSES = ["Knockout Flagged", "Deadline Missed"];
    // A genuinely custom hiring flow on THIS job (#3186) — the FE mirror of the BE
    // `has_custom_flow(job)` that gates the flagged split. Requires the per-company
    // rollout flag AND a custom (non-default) flow. Absent ⇒ OFF, so default jobs
    // never get a Flagged tab and their Screening tab is byte-identical to today.
    const isCustomFlowJob = hiringFlowEnabled && job?.hiring_flow_is_custom === true;

    // Screening status split for the tab badges — loaded independently of the active
    // tab so the "Screening In Progress" (qualified) and "Flagged" badges are correct
    // from any tab (#3186).
    const [screeningGroupStatus, setScreeningGroupStatus] = useState<ApplicantStatusCount[]>([]);
    const refreshScreeningCounts = () => {
        if (!hiringFlowEnabled || !job?.id) return;
        getJobApplicantGroup(job.id, "", "pending_assessment").then((resp) => {
            setScreeningGroupStatus(resp.data.data || []);
        }).catch(() => { /* badge-only; ignore */ });
    };
    useEffect(() => {
        refreshScreeningCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hiringFlowEnabled, job?.id]);
    const clientFlaggedCount = screeningGroupStatus
        .filter((s) => FLOW_FLAG_STATUSES.includes(s.status))
        .reduce((sum, s) => sum + (s.count || 0), 0);
    const clientPendingTotal = screeningGroupStatus.reduce((sum, s) => sum + (s.count || 0), 0);
    // Prefer the server-side split from /applicants-count (`sub_view_counts`, present
    // only for flow jobs, loaded on mount and refreshed on every action); fall back to
    // the client-side status split for older BE payloads.
    const flaggedTabCount = funnelCounts.sub_view_counts?.flagged ?? funnelCounts.flagged ?? clientFlaggedCount;
    const qualifiedTabCount = funnelCounts.sub_view_counts?.qualified ?? Math.max(0, clientPendingTotal - clientFlaggedCount);
    // Show the Flagged tab whenever the job has a hiring flow — hidden only for
    // non-flow jobs, and NOT gated on the flagged count (stays visible at 0, like the
    // other funnel tabs). `sub_view_counts` is the authoritative signal: it's `null`
    // for non-flow jobs and is loaded on mount with the funnel counts, so the tab is
    // correct from ANY tab — unlike `isFlowJob`, which is sticky and only flips true
    // after a tab whose rows carry flow metadata has loaded. Keep isFlowJob /
    // isCustomFlowJob as fallbacks for older BE payloads.
    const isFlowFlaggedJob = funnelCounts.sub_view_counts != null || isFlowJob || isCustomFlowJob;
    // The job runs a WhatsApp-first hiring flow — enables the "Reject by WhatsApp"
    // bulk action (candidates are reachable over WhatsApp, not necessarily email).
    const isWaHiringFlow =
        (job as (Job & { hiring_flow?: { channel?: string } }) | null)?.hiring_flow?.channel === "whatsapp_first"
        || applicants.some((a) => a.channel === "whatsapp_first");

    // After a flow action (Advance / gate resolve / reject) the candidate moved
    // server-side — reload the current tab (onSearch maps flagged/qualified) + counts.
    const onFlowActionReload = () => {
        onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
        onLoadApplicationCount(job?.id || "");
        refreshScreeningCounts();
    };
    // ---------------------------------------------------------------------------

    // Ids of visible candidates whose similarity score isn't computed yet ("-" / null).
    // Scores are produced asynchronously on the BE (embeddings + LLM, longer after a bulk
    // upload) — these are the cards that should still show the "scoring…" spinner.
    const pendingScoreIds = useMemo(
        () =>
            applicants
                .filter(
                    (a) =>
                        a.similarity_score === "-" ||
                        a.similarity_score === null ||
                        a.similarity_score === undefined
                )
                .map((a) => a.applicant_id),
        [applicants]
    );
    // Stable dependency so the interval only re-subscribes when the SET of pending ids
    // changes (not on every applicants mutation).
    const pendingScoreKey = pendingScoreIds.join(",");

    // Poll the lightweight scores endpoint (~2s) while any visible card is still scoring,
    // so scores appear live without refetching the full (expensive) /applicants list.
    // Stops automatically once every visible card is ready (pendingScoreIds empties) and
    // resumes when new unscored cards appear (bulk upload / pagination).
    useEffect(() => {
        if (!job?.id || pendingScoreIds.length === 0) return;

        let cancelled = false;

        const pollScores = () => {
            getApplicantsScores(job.id, pendingScoreIds)
                .then((scores) => {
                    if (cancelled || !scores || scores.length === 0) return;

                    const readyScores: Record<string, number> = {};
                    scores.forEach((s) => {
                        if (s.ready && s.similarity_score !== null && s.similarity_score !== undefined) {
                            readyScores[s.candidate_id] = s.similarity_score;
                        }
                    });

                    if (Object.keys(readyScores).length === 0) return;

                    setApplicants((prev) =>
                        prev.map((a) =>
                            readyScores[a.applicant_id] !== undefined
                                ? { ...a, similarity_score: readyScores[a.applicant_id] }
                                : a
                        )
                    );
                    // Feeds the match-score chip in the drawer, which reads from this map.
                    setSimilarityScoreCandidate((prev) => ({ ...prev, ...readyScores }));
                    // Keep the open drawer's candidate in sync so its score updates live too.
                    setSelectedApplicant((prev) =>
                        prev && readyScores[prev.applicant_id] !== undefined
                            ? { ...prev, similarity_score: readyScores[prev.applicant_id] }
                            : prev
                    );
                })
                .catch((err) => {
                    catchErrorLog(`Failed to poll applicant scores: ${err}`);
                });
        };

        const intervalId = setInterval(pollScores, 2000);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.id, pendingScoreKey]);

    useEffect(() => {
        if (currentUserInfo) {
            getShortlistedCandidates().then((resp) => {
                if (resp.status === 200) {
                    setCookie(`shortlisted_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                }
            })
        }
    }, [currentUserInfo])

    useEffect(() => {
        if (reloadCount && job && reloadCount > 0) {
            onLoadApplicationCount(job?.id || "");
            setActiveFunnelStage("uploads");
            onSearch(defaultPagination.page, searchQuery, getFunnelStatus("uploads"), matchFilter.value, false, "uploads");
        }
    }, [reloadCount, job])

    // ?view=<drawer tab> deep link (e.g. ?tab=to_review&candidate=<id>&view=messages):
    // `tab` stays the funnel stage as usual; `view` queues the candidate drawer
    // to auto-open on that tab once the first rows land, with `candidate`
    // picking which one (first row otherwise). Ref seeded from the URL at
    // mount: it must survive the async list load and fire only once.
    const pendingDrawerView = useRef<string | null>(params.get("view"));

    useEffect(() => {
        const tab = params.get("tab");
        if (job) {
            setApplicants([]);
            if (tab) {
                setActiveFunnelStage(tab);
                getApplicantCount(job?.id || "").then((resp) => {
                    if (resp.status === 200) {
                        setFunnelCounts(resp.data.data);
                    }
                }).catch((err) => {
                    catchErrorLog(`Failed to fetch applicant count for job:`, err);
                })
                if (tab === "insights") {
                    setLoading(false);
                    return;
                };
                onSearch(defaultPagination.page, searchQuery, getFunnelStatus(tab), matchFilter.value, false, tab);
            } else {
                getApplicantCount(job?.id || "").then((resp) => {
                    if (resp.status === 200) {
                        const counts = resp.data.data;
                        setFunnelCounts(counts);
                        // #3252: WhatsApp-first jobs often have an empty Applied tab —
                        // open on Ready for Review when Applied is 0 and it has rows.
                        // Initial selection only: once the user picks a tab it goes to
                        // the URL (?tab=…) and this branch never runs again.
                        const initialTab = (counts?.applied ?? 0) === 0 && (counts?.to_review ?? 0) > 0 ? "to_review" : "applied";
                        setActiveFunnelStage(initialTab);
                        onSearch(defaultPagination.page, searchQuery, getFunnelStatus(initialTab), matchFilter.value, false, initialTab);
                    }
                }).catch((err) => {
                    catchErrorLog(`Failed to fetch applicant count for job:`, err);
                })
            }
        }
    }, [params.get("tab"), job])

    // ?view= deep link (see above): open the drawer once the applicants are in.
    useEffect(() => {
        const view = pendingDrawerView.current;
        if (!view || applicants.length === 0) return;
        pendingDrawerView.current = null;
        const wanted = params.get("candidate");
        const target = (wanted && applicants.find((a) => a.applicant_id === wanted || a.id === wanted)) || applicants[0];
        setFocusComposerOnOpen(false);
        setDrawerActiveTab(view);
        setSelectedApplicant(target);
        setShowCandidateDrawer(true);
    }, [applicants]);

    const reloadShortlistedCandidate = () => {
        getShortlistedCandidates().then((resp) => {
            if (resp.status === 200) {
                setCookie(`shortlisted_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
            }
        })
    }

    // #3316: silent replay of the last list fetch + funnel counts. Queued (not
    // dropped) while a bulk selection is active so the refresh can't clobber it.
    const silentSseRefresh = () => {
        const last = lastSearchRef.current;
        if (!last || !job?.id) return;
        if (applicants.some((a) => a.is_selected)) {
            pendingSseRefreshRef.current = true;
            return;
        }
        onSearch(last.page, last.keyword, last.application_status, last.matchLevel, last.isBadgeFilter, last.funnelStage, true);
        getApplicantCount(job.id).then((resp) => {
            if (resp.status === 200) setFunnelCounts(resp.data.data);
        }).catch(() => undefined);
    };
    // Ref indirection: the SSE effect subscribes once per job, but each event
    // must see the CURRENT view state (page/filters/selection), not the state
    // captured when the stream opened.
    silentSseRefreshRef.current = silentSseRefresh;

    // #3316 (BE ourteam-backend#4092): live board — subscribe to the per-job
    // SSE change stream on flow jobs; each signal debounces into one silent
    // refetch. The service degrades permanently (manual behavior) when the
    // endpoint is absent, so this is safe ahead of the BE.
    useEffect(() => {
        if (!job?.id || !hiringFlowEnabled) return;
        const controller = new AbortController();
        subscribeApplicantEvents(job.id, {
            signal: controller.signal,
            onEvent: () => {
                if (sseDebounceRef.current) clearTimeout(sseDebounceRef.current);
                sseDebounceRef.current = setTimeout(() => silentSseRefreshRef.current(), 1500);
            },
            // A drop may have swallowed events — refetch once on reconnect.
            onReconnect: () => silentSseRefreshRef.current(),
        });
        return () => {
            controller.abort();
            if (sseDebounceRef.current) clearTimeout(sseDebounceRef.current);
        };
    }, [job?.id, hiringFlowEnabled]);

    // Bulk selection cleared → run the refresh that was queued during it.
    useEffect(() => {
        if (!pendingSseRefreshRef.current) return;
        if (applicants.some((a) => a.is_selected)) return;
        pendingSseRefreshRef.current = false;
        silentSseRefreshRef.current();
    }, [applicants]);

    const handleViewProfile = (applicant: Applicant) => {
        setFocusComposerOnOpen(false);
        // #3256: on a hiring-flow job the WhatsApp thread IS the process — open
        // the drawer on Messages first; non-flow jobs keep Profile.
        setDrawerActiveTab(isFlowFlaggedJob ? "messages" : "profile");
        setShowCandidateDrawer(true);
        setSelectedApplicant(applicant);
    };

    const handleViewReview = (applicant: Applicant) => {
        setFocusComposerOnOpen(true);
        setDrawerActiveTab("review");
        setShowCandidateDrawer(true);
        setSelectedApplicant(applicant);
    };

    const handleCloseProfile = () => {
        setShowCandidateDrawer(false);
        setSelectedApplicant(null);
        setFocusComposerOnOpen(false);
    };

    const handleSearchApplication = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSearch(1, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
        setChipStatusFilter("");
    }

    // Toggle dropdown visibility
    const toggleDropdown = (dropdown: string) => {
        setOpenDropdown(openDropdown === dropdown ? null : dropdown);
    };

    // Handle match level selection
    const handleMatchLevelSelect = (matchLevel: MatchLevelItem) => {
        setMatchFilter(matchLevel);
        setOpenDropdown(null);

        onSearch(1, searchQuery, getFunnelStatus(activeFunnelStage), matchLevel.value || "");
    };

    const handleSortBy = (field: string) => {
        if (sortField !== field) {
            return <ArrowUpDownIcon className="h-4 w-4 text-gray-400" />;
        }
        if (sortDirection === "asc") {
            return <ArrowUpIcon className="h-4 w-4 text-gray-600" />;
        }
        if (sortDirection === "desc") {
            return <ArrowDownIcon className="h-4 w-4 text-gray-600" />;
        }
        return <ArrowUpDownIcon className="h-4 w-4 text-gray-400" />;
    }

    const handleSortDirection = (field: string, direction: "asc" | "desc" | "") => {
        setSortDirection(direction);
        setSortField(field);
        getJobApplicants({
            job_id: job?.id || "",
            page: currentPage,
            limit: LIMIT,
            keyword: searchQuery,
            application_status: chipStatusFilter ? chipStatusFilter : "",
            match_level: matchFilter.value !== "all" ? matchFilter.value : "",
            sort_by: field,
            sort: direction,
            funnel_status: activeFunnelStage,
        }).then((resp) => {
            setApplicants(resp.data.data);
            setMetaPagination(resp.data.meta.pagination);
        }).catch((err) => {
            catchErrorLog(`Failed to fetch applicants for job:`, err);
            setApplicants([]);
        }).finally(() => {
            setLoading(false);
        })
    }

    const onClearSearch = () => {
        setSearchQuery("");
        onSearch(1, "", getFunnelStatus(activeFunnelStage), matchFilter.value || "");
    }

    const onChangeActiveFunnelStage = (stage: string) => {
        setActiveFunnelStage(stage);
        setShowCandidateDrawer(false);

        const url = new URL(window.location.href);
        url.searchParams.set("tab", stage);
        window.history.pushState({}, "", url.toString());
    }

    const onSelectAllApplicants = (checked: boolean) => {
        setApplicants((prevApplicants) =>
            prevApplicants.map((applicant) => ({
                ...applicant,
                is_selected: checked,
            }))
        );
    }

    const onClearSelection = () => {
        setApplicants((prevApplicants) =>
            prevApplicants.map((applicant) => ({
                ...applicant,
                is_selected: false,
            }))
        );
    }

    const onMarkApplicantStatus = (applicant_ids: string[], status: string) => {
        applicantUpdateStatus(job?.id || "", {
            user_ids: applicant_ids,
            status: convertCapitalizeWords(status),
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${convertCapitalizeWords(status)}`);
                if (status === "offer_sent" || status === "pending_review" || status === "hired" || status === "archived" || status === "moved") {
                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                    onLoadApplicationCount(job?.id || "");
                } else {
                    setApplicants((prevApplicants) =>
                        prevApplicants.map((app) =>
                            applicant_ids.includes(app.applicant_id)
                                ? { ...app, status: convertToLowerSnakeCase(convertCapitalizeWords(status)) }
                                : app
                        )
                    );
                }
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status. Please try again.");
        })
    }

    const handleSendInterviewInvitation = async (body: SendCustomizedEmailRequest, reviewScheduledNotes: ReviewScheduledNoteItem) => {
        sendCustomizedEmailV2({
            ...body,
            review_schedule_notes: reviewScheduledNotes,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Review invitation successfully sent.");
                setApplicants((prevApplicants) =>
                    prevApplicants.map((app) =>
                        (body.user_ids || []).includes(app.applicant_id)
                            ? { ...app, status: "Review Scheduled" }
                            : app
                    )
                );
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send interview invitation: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            } else {
                snackbar.showError("Failed to send interview invitation. Please try again.");
            }
        }).finally(() => {
            setShowInterviewInvitationDialog(false);
        })
    };

    const handleRejectCandidate = async (body: SendCustomizedEmailRequest) => {
        sendCustomizedEmailV2(body).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess("Rejection email sent successfully.");
                onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                onLoadApplicationCount(job?.id || "");
                refreshScreeningCounts();
            }
        }).catch((error) => {
            catchErrorLog(`Failed to reject candidate: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            } else {
                snackbar.showError("Failed to reject candidate. Please try again.");
            }
        }).finally(() => {
            setShowRejectOfferDialog(false);
        })
    };

    // #3186: reject a single flagged candidate. Candidates WITH an email get the
    // existing email reject dialog; email-less (WhatsApp-first / CV-less) candidates
    // get the WhatsApp reject dialog instead (#3877), since the email can't reach them.
    const handleRejectFlagged = (applicant: Applicant) => {
        setApplicants((prev) => prev.map((a) => ({ ...a, is_selected: a.applicant_id === applicant.applicant_id })));
        setSelectedApplicant(applicant);
        const hasNoEmail = !applicant.main_email || applicant.main_email.trim() === "";
        if (hasNoEmail) {
            setShowWaRejectDialog(true);
        } else {
            setShowRejectOfferDialog(true);
        }
    };

    const handleSendJobOffer = async (body: CreateContractRequest) => {
        createContractV2(body).then((resp) => {
            if (resp.status === 201) {
                snackbar.showSuccess('Job offer(s) sent successfully.');
                onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                onLoadApplicationCount(job?.id || "");
            } else {
                snackbar.showError(resp.data.message);
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send job offer: ${error}`);
            snackbar.showError("Failed to send screening invitation. Please try again.");
        }).finally(() => {
            setShowJobOfferDialog(false);
        })
    };

    const handleSendAssessmentInvitation = (body: SendCustomizedEmailRequest, type: string) => {
        sendCustomizedEmailV2(body).then((resp) => {
            if (resp.status === 200) {
                return applicantUpdateStatus(job?.id || "", {
                    user_ids: body.user_ids || [],
                    status: "Pending Assessment",
                }).then((resp) => {
                    if (resp.status === 200) {
                        if (type === "reminder") {
                            snackbar.showSuccess("Screening reminder successfully sent.");
                        } else if (type === "resend-invitation") {
                            snackbar.showSuccess("Screening link successfully sent.");
                        } else if (type === "retake") {
                            snackbar.showSuccess("Retake interview request sent successfully.");
                        } else {
                            snackbar.showSuccess("Screening invitation successfully sent.");
                        }
                        onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                        onLoadApplicationCount(job?.id || "");
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to update applicant status badge: ${error}`);
                    snackbar.showError("Failed to send screening invitation. Please try again.");
                })
            }
        }).catch((error) => {
            catchErrorLog(`Failed to send assessment invitation: ${error}`);
            if (error.response?.status === 400 && (error.response?.data?.is_usage_limit_reached || error.response?.data?.is_trial_end_soon_usage_reached)) {
                showTrialUsageLimitDialog(!!error.response?.data?.is_trial_end_soon_usage_reached);
            } else if (error.response?.data?.message) {
                snackbar.showError(error.response.data.message);
            } else {
                snackbar.showError("Failed to send screening invitation. Please try again.");
            }
        }).finally(() => {
            setShowAssessmentInvitationDialog(false);
            setShowAssessmentInvitationReminderDialog(false);
            setShowAssessmentRetakeDialog(false);
            setShowAssessmentResendDialog(false);
        });
    }

    const handleMarkSendOffer = (applicant_ids: string[], note: string) => {
        applicantUpdateStatus(job?.id || "", {
            user_ids: applicant_ids,
            status: convertCapitalizeWords(isMarkOfferResent ? "offer_resent" : "offer_sent"),
            offer_notes: note,
        }).then((resp) => {
            if (resp.status === 200) {
                snackbar.showSuccess(`Status updated to ${convertCapitalizeWords(isMarkOfferResent ? "offer_resent" : "offer_sent")}`);
                onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                onLoadApplicationCount(job?.id || "");
            }
        }).catch((error) => {
            catchErrorLog(`failed to update applicant status badge: ${error}`);
            snackbar.showError("Failed to update applicant status to offer sent. Please try again.");
        })
    }

    const handleRequestDeleteCandidate = (targets: Applicant[]) => {
        setDeleteCandidateTargets(targets);
        if (isDeleteConfirmSuppressed()) {
            handleConfirmDeleteCandidates(targets);
        } else {
            setIsDeleteDialogOpen(true);
        }
    };

    const handleConfirmDeleteCandidates = (targets?: Applicant[]) => {
        const toDelete = targets || deleteCandidateTargets;
        const applicantIds = toDelete.map((a) => a.applicant_id);

        setIsDeletingCandidates(true);

        deleteApplicants(job?.id || "", applicantIds)
            .then((resp) => {
                if (resp.status === 200) {
                    setApplicants((prev) =>
                        prev.filter((a) => !applicantIds.includes(a.applicant_id))
                    );
                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                    onLoadApplicationCount(job?.id || "");
                    onClearSelection();

                    snackbar.showSuccess(
                        `${toDelete.length} ${toDelete.length === 1 ? "candidate" : "candidates"} successfully deleted.`,
                        undefined,
                        8000,
                        {
                            actionLabel: "Undo",
                            onAction: () => handleUndoDelete(applicantIds),
                        }
                    );
                }
            })
            .catch((error) => {
                catchErrorLog(`Failed to delete candidates: ${error}`);
                snackbar.showError("Failed to delete candidate(s). Please try again.");
            })
            .finally(() => {
                setIsDeletingCandidates(false);
                setIsDeleteDialogOpen(false);
                setDeleteCandidateTargets([]);
            });
    };

    const handleUndoDelete = (applicantIds: string[]) => {
        restoreApplicants(job?.id || "", applicantIds)
            .then((resp) => {
                if (resp.status === 200) {
                    snackbar.showSuccess("Candidate(s) restored successfully.");
                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
                    onLoadApplicationCount(job?.id || "");
                }
            })
            .catch((error) => {
                catchErrorLog(`Failed to restore candidates: ${error}`);
                snackbar.showError("Failed to restore candidate(s). Please try again.");
            });
    };

    const getSelectedApplicantsStatuses = (): Set<string> => {
        const statuses = new Set<string>();
        applicants.filter(app => app.is_selected).forEach((item) => {
            const applicant = applicants.find((app) => app.applicant_id === item.applicant_id);
            if (applicant) {
                statuses.add(applicant.status);
            }
        });
        return statuses;
    };

    const isAllSelected =
        applicants.length > 0 &&
        applicants.every((app) => app.is_selected);

    return (
        <div className="bg-white dark:bg-gray-800 relative">
            <div className="pb-4">
                <div className="w-full flex flex-col space-y-2">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex items-center flex-wrap gap-2">
                            <h2 className="text-2xl font-semibold dark:text-white">
                                Applicants for {job?.job_title}
                            </h2>
                            {(job?.is_enable_auto_rejection) && (
                                <Tooltip
                                    side="bottom"
                                    content={(
                                        <p className="text-sm">
                                            Auto-rejection is enabled. Candidates will be
                                            rejected automatically if no action is taken
                                            within {job?.days_auto_rejection || 0} days.
                                            <br />
                                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 inline-block">
                                                Click to edit.
                                            </span>
                                        </p>
                                    )}
                                >
                                    <div
                                        onClick={() => navigate(`/jobs/edit/${job?.id || ""}`)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-full cursor-pointer hover:bg-red-200 transition-colors"
                                    >
                                        <TimerIcon className="h-3.5 w-3.5 text-red-600" />
                                        <span className="text-xs font-medium text-red-700 dark:text-red-600">
                                            {job?.days_auto_rejection || 0} days
                                        </span>
                                    </div>
                                </Tooltip>
                            )}
                            {(job?.is_enable_application_expiry) && (
                                <Tooltip
                                    side="bottom"
                                    content={(
                                        <p className="text-sm">
                                            All unique screening links are set to expire
                                            within {job?.application_expiry_days} days.
                                            <br />
                                            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 inline-block">
                                                Click to edit.
                                            </span>
                                        </p>
                                    )}
                                >
                                    <div
                                        onClick={() => navigate(`/jobs/edit/${job?.id || ""}`)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
                                        style={{ border: "0.909091px solid #bfdbfe" }}
                                    >
                                        <ClockIcon className="h-3.5 w-3.5 text-blue-600" />
                                        <span className="text-xs font-medium text-blue-700 dark:text-blue-600">
                                            {job?.application_expiry_days || 7} days
                                        </span>
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* "Get WhatsApp ad link" moved to the Job list row menu as
                                "Copy WhatsApp link" (#3251) — whatsapp_first jobs only. */}
                            <Button
                                variant="outline"
                                className="border-gray-300 dark:border-gray-700"
                                disabled={isSubscriptionLimitReached}
                                onClick={() => {
                                    if (!isProcessing) {
                                        setIsBulkCVUploadDialogOpen(true);
                                    } else {
                                        setIsVisible(true);
                                    }
                                }}
                            >
                                {isProcessing ? (
                                    <>
                                        <div className="w-4 h-4 mr-2 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <UploadIcon className="w-4 h-4" />
                                        Upload Bulk CVs
                                    </>
                                )}
                            </Button>
                            {/* Sourcing is a write funnel — every route out of that
                                screen invites or adds a candidate — so it closes with
                                the rest of the actions during a window (#3573). Plain
                                reading of this page keeps working. */}
                            <span className={maintenance.groupClass} {...maintenance.groupProps}>
                                <Link to={`/jobs/${job?.id || ""}/candidates`}>
                                    <Button variant="default">
                                        <UserPlusIcon className="h-4 w-4" />
                                        View Suggested Candidates
                                    </Button>
                                </Link>
                            </span>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        <b>{funnelCounts.total_applicant || 0} {funnelCounts.total_applicant === 1 ? "candidate" : "candidates"}</b> have applied for this position
                    </p>
                </div>
            </div>
            {/* Funnel Stage Tabs using Tabs component */}
            <div className="border-b border-gray-200 dark:border-gray-800 mt-4 mb-2">
                <Tabs value={activeFunnelStage} onValueChange={onChangeActiveFunnelStage} className="w-full">
                    <TabsList className="flex w-full bg-transparent h-auto p-0 border-b border-gray-200 dark:border-gray-800 justify-start rounded-none overflow-x-auto scrollbar-hide">
                        {(funnelCounts.uploads > 0 || funnelCounts.show_upload_tab) ? (
                            <TabsTrigger
                                disabled={isLoading}
                                value="uploads"
                                className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                            >
                                <UploadIcon className="h-4 w-4" />
                                Uploads
                                {funnelCounts.uploads > 0 && (
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                            activeFunnelStage === "uploads"
                                                ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                        )}
                                    >
                                        {funnelCounts.uploads}
                                    </Badge>
                                )}
                            </TabsTrigger>
                        ) : null}
                        <TabsTrigger
                            disabled={isLoading}
                            value="applied"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <FileIcon className="h-4 w-4" />
                            Applied
                            {funnelCounts.applied > 0 && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                        activeFunnelStage === "applied"
                                            ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                            : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    )}
                                >
                                    {funnelCounts.applied}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            disabled={isLoading}
                            value="pending_assessment"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <ClipboardListIcon className="h-4 w-4" />
                            Screening In Progress
                            {(hiringFlowEnabled ? qualifiedTabCount : funnelCounts.pending_assessment) > 0 && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                        activeFunnelStage === "pending_assessment"
                                            ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                            : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    )}
                                >
                                    {hiringFlowEnabled ? qualifiedTabCount : funnelCounts.pending_assessment}
                                </Badge>
                            )}
                        </TabsTrigger>
                        {/* Flagged tab (#3186) — flow jobs only. Holds gate-held candidates
                            (Knockout Flagged · Deadline Missed), moved out of Screening. */}
                        {isFlowFlaggedJob && (
                            <TabsTrigger
                                disabled={isLoading}
                                value="flagged"
                                className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                            >
                                <AlertTriangleIcon className="h-4 w-4" />
                                Flagged
                                {flaggedTabCount > 0 && (
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            "ml-1 group-hover:bg-amber-100 group-hover:text-amber-700",
                                            activeFunnelStage === "flagged"
                                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                                        )}
                                    >
                                        {flaggedTabCount}
                                    </Badge>
                                )}
                            </TabsTrigger>
                        )}
                        <TabsTrigger
                            disabled={isLoading}
                            value="to_review"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <SearchCheckIcon className="h-4 w-4" />
                            Ready for Review
                            {funnelCounts.to_review > 0 && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                        activeFunnelStage === "to_review"
                                            ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                            : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    )}
                                >
                                    {funnelCounts.to_review}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            disabled={isLoading}
                            value="offers"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <BriefcaseIcon className="h-4 w-4" />
                            Offers
                            {funnelCounts.offers > 0 && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                        activeFunnelStage === "offers"
                                            ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                            : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    )}
                                >
                                    {funnelCounts.offers}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            disabled={isLoading}
                            value="closed"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <XCircleIcon className="h-4 w-4" />
                            Closed
                            {funnelCounts.closed > 0 && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 group-hover:bg-blue-100 group-hover:text-blue-600",
                                        activeFunnelStage === "closed"
                                            ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                                            : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                    )}
                                >
                                    {funnelCounts.closed}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            disabled={isLoading}
                            value="insights"
                            className="group text-sm flex items-center gap-1 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap"
                        >
                            <ChartNoAxesColumn className="h-4 w-4" />
                            Insights
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            <div className="flex items-center justify-between">
                <div>
                    {activeFunnelStage === "uploads" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Uploads:</b> Candidates added via bulk CV uploads and awaiting review.</p>
                    ) : activeFunnelStage === "applied" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Applied:</b> Candidate has applied and is awaiting a screening invitation.</p>
                    ) : activeFunnelStage === "pending_assessment" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Screening In Progress:</b> Candidate is completing their screening (AI interview, forms, or eligibility checks).</p>
                    ) : activeFunnelStage === "flagged" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Flagged:</b> Candidates paused by a hiring-flow gate (a knockout answer or a missed deadline). Review, then keep, reject, or archive them.</p>
                    ) : activeFunnelStage === "to_review" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Ready to Review:</b> The screening is completed and ready for your review to decide next steps.</p>
                    ) : activeFunnelStage === "offers" ? (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Offers:</b> Candidate is in the offer stage — Manage offer communication and outcome.</p>
                    ) : activeFunnelStage === "closed" && (
                        <p className="text-gray-700 text-sm py-2"><b className="text-black">Closed:</b> Candidate process is complete — Hired, rejected, or role closed.</p>
                    )}
                </div>
                {(!["insights"].includes(activeFunnelStage) && applicantGroupStatus.length > 0) && (
                    <div className="flex flex-row gap-2 items-center flex-shrink-0">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            Filter by status:
                        </span>
                        <Select
                            value={chipStatusFilter || "all"}
                            onValueChange={(value) => {
                                if (value === "all") {
                                    setChipStatusFilter("");
                                    onFilterStatus("");
                                } else {
                                    setChipStatusFilter(value);
                                    onFilterStatus(value);
                                }
                            }}
                        >
                            <SelectTrigger className="h-8 w-[200px] text-sm">
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                {(applicantGroupStatus || []).map((applicant_status) => {
                                    const badge = getApplicantStatusBadge(convertToLowerSnakeCase(applicant_status.status));
                                    return (
                                        <SelectItem key={applicant_status.status} value={applicant_status.status}>
                                            {badge.label} <span className="text-gray-400">({applicant_status.count})</span>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
            {activeFunnelStage !== "insights" ? (
                <>
                    <div className="py-4 flex sm:flex-row flex-col flex-wrap gap-2">
                        <form onSubmit={handleSearchApplication} className="relative flex-1">
                            <SearchIcon
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                            />

                            <Input
                                className="pl-9 w-full"
                                placeholder="Search applicants by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button type="submit" className="hidden">Find</button>

                            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={onClearSearch}
                                        className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                        aria-label="Clear search"
                                    >
                                        <XIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </form>

                        <div className="flex sm:flex-row flex-col gap-2">
                            <div className="relative dropdown-container">
                                <Button
                                    variant="outline"
                                    className="flex items-center justify-between sm:w-40 text-left pl-3 w-full"
                                    onClick={() => toggleDropdown("match")}
                                >
                                    <span>{matchFilter.label}</span>
                                    <ChevronDown size={16} className="ml-2" />
                                </Button>
                                {openDropdown === "match" && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                                        <div className="py-1">
                                            {matchLevelApplicantOptions.map((level, index) => (
                                                <div
                                                    key={index}
                                                    className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${matchFilter.value === level.value ? "bg-gray-50 font-medium" : ""
                                                        }`}
                                                    onClick={() => handleMatchLevelSelect(level)}
                                                >
                                                    {level.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* #3186: the Qualified/Flagged sub-view toggle was promoted to a
                        dedicated "Flagged" tab (flow jobs only) — Screening now lists
                        only qualified candidates, so no in-tab toggle is needed. */}
                    <div className="overflow-x-auto">
                        {activeFunnelStage !== "uploads" ? (
                            <TableListApplicant
                                isLoading={isLoading}
                                applicants={applicants}
                                job={job}
                                activeFunnelStage={activeFunnelStage}
                                showFlowColumns={isFlowJob || activeFunnelStage === "flagged"}
                                onFlowAction={onFlowActionReload}
                                onRejectFlagged={handleRejectFlagged}
                                isAllSelected={isAllSelected}
                                sortDirection={sortDirection}
                                shortlistedCountMap={shortlistedCountMap}
                                commentCountMap={commentCountMap}
                                onSelectAllApplicants={onSelectAllApplicants}
                                onSortDirection={handleSortDirection}
                                onSortBy={handleSortBy}
                                onViewProfile={handleViewProfile}
                                onViewReview={handleViewReview}
                                onShowRejectReason={(applicant) => {
                                    setShowRejectReasonDialog(true);
                                    setSelectedApplicant(applicant);
                                }}
                                onUpdateScoreSimilarity={(applicantID: string, score: number) => {
                                    setSimilarityScoreCandidate((prev) => ({
                                        ...prev,
                                        [applicantID]: score,
                                    }));
                                }}
                                onSelectedApplicant={(applicantID: string, isSelected: boolean) => {
                                    setApplicants((prevApplicants) =>
                                        prevApplicants.map((app) =>
                                            app.applicant_id === applicantID
                                                ? { ...app, is_selected: isSelected }
                                                : app
                                        )
                                    );
                                }}
                                onApplicantStatusChange={(applicantID: string, status: string) => {
                                    if (status === "offer_sent" || status === "review_scheduled") {
                                        onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
                                        onLoadApplicationCount(job?.id || "");
                                    } else {
                                        setApplicants((prevApplicants) =>
                                            prevApplicants.map((app) =>
                                                app.applicant_id === applicantID
                                                    ? { ...app, status: status }
                                                    : app
                                            )
                                        );
                                    }
                                }}
                                onDeleteCandidate={(applicant) => handleRequestDeleteCandidate([applicant])}
                            />
                        ) : (
                            <TableListUpload
                                isLoading={isLoading}
                                applicants={applicants}
                                job={job}
                                isAllSelected={isAllSelected}
                                sortDirection={sortDirection}
                                shortlistedCountMap={shortlistedCountMap}
                                commentCountMap={commentCountMap}
                                onSelectAllApplicants={onSelectAllApplicants}
                                onSortDirection={handleSortDirection}
                                onSortBy={handleSortBy}
                                onViewProfile={handleViewProfile}
                                onViewReview={handleViewReview}
                                onSelectedApplicant={(applicantID: string, isSelected: boolean) => {
                                    setApplicants((prevApplicants) =>
                                        prevApplicants.map((app) =>
                                            app.applicant_id === applicantID
                                                ? { ...app, is_selected: isSelected }
                                                : app
                                        )
                                    );
                                }}
                                onUpdateScoreSimilarity={(applicantID: string, score: number) => {
                                    setSimilarityScoreCandidate((prev) => ({
                                        ...prev,
                                        [applicantID]: score,
                                    }));
                                }}
                                onViewCV={(applicant: Applicant) => {
                                    setDrawerActiveTab("cv");
                                    setShowCandidateDrawer(true);
                                    setSelectedApplicant(applicant);
                                }}
                                onReload={() => {
                                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
                                }}
                                onApplicantStatusChange={(applicantID: string, status: string) => {
                                    setApplicants((prevApplicants) =>
                                        prevApplicants.map((app) =>
                                            app.applicant_id === applicantID
                                                ? { ...app, status: status }
                                                : app
                                        )
                                    );
                                }}
                                onDeleteCandidate={(applicant) => handleRequestDeleteCandidate([applicant])}
                            />
                        )}
                        <div className="p-4">
                            <Pagination
                                isLoading={isLoading}
                                currentPage={currentPage}
                                totalPages={metaPagination.total_pages || 0}
                                onPageChange={onLoadPagination}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <div className="mt-4">
                    <ApplicantInsights job_id={job?.id || ""} />
                </div>
            )}

            {(isShowCandidateDrawer && selectedApplicant) && (
                <DrawerCandidateProfile
                    activeFunnelStage={activeFunnelStage}
                    isShowHireButton
                    isShowPercentMatch
                    isOpen={!!selectedApplicant}
                    selectedActiveTab={drawerActiveTab}
                    cvID={selectedApplicant.cv_id}
                    candidate={selectedApplicant.cv_data}
                    applicant={selectedApplicant}
                    job={job}
                    user={{
                        id: selectedApplicant.applicant_id,
                        first_name: selectedApplicant.applicant_name,
                        profile_picture_path: selectedApplicant.profile_picture_path || null,
                        applied_to_job_id: selectedApplicant.applied_to_job_id || [],
                        cv_id: selectedApplicant.cv_id,
                        email: selectedApplicant.main_email || "",
                        country_code: selectedApplicant.country_code || "",
                        phone_number: selectedApplicant.phone_number || "",
                        last_name: "",
                        verification_status: "",
                        role: "",
                        username: "",
                        created_at: "",
                    }}
                    applicant_status={selectedApplicant ? getApplicantStatusBadge(convertToLowerSnakeCase(selectedApplicant.status)).label : ""}
                    similarity_score={Number(similarityScoreCandidate[selectedApplicant.applicant_id] ?? selectedApplicant.similarity_score) || 0}
                    autoFocusComposer={focusComposerOnOpen}
                    onClose={handleCloseProfile}
                    onReload={() => {
                        reloadShortlistedCandidate();
                        onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
                        onLoadApplicationCount(job?.id || "");
                    }}
                />
            )}

            {(selectedApplicant) && (
                <RejectionReasonDialog
                    open={isShowRejectReasonDialog}
                    onOpenChange={setShowRejectReasonDialog}
                    applicant={selectedApplicant}
                />
            )}

            <BulkApplicationActionsBar
                applicants={applicants.filter(applicant => applicant.is_selected)}
                currentStage={activeFunnelStage}
                selectedCount={applicants.filter(applicant => applicant.is_selected).length}
                onClearSelection={onClearSelection}
                selectedApplicantsStages={new Set()}
                selectedApplicantsStatuses={getSelectedApplicantsStatuses()}
                onSendInterview={() => setShowInterviewInvitationDialog(true)}
                onHire={() => setShowJobOfferDialog(true)}
                onReject={() => setShowRejectOfferDialog(true)}
                isWaHiringFlow={isWaHiringFlow}
                onRejectByWa={() => setShowWaRejectDialog(true)}
                onSendAssessment={() => setShowAssessmentInvitationDialog(true)}
                onSendAssessmentViaWhatsapp={() => setShowWaInvitationDialog(true)}
                onSendAssessmentReminder={() => setShowAssessmentInvitationReminderDialog(true)}
                onRequestRetake={() => setShowAssessmentRetakeDialog(true)}
                onResendInvitation={() => setShowAssessmentResendDialog(true)}
                assessmentReminderCount={applicants.filter(applicant => applicant.is_selected && applicant.status !== "Link Expired").length}
                interviewRetakeCount={applicants.filter(applicant => applicant.is_selected && (applicant.status === "Partial Interview" || applicant.status === "Aborted Interview" || applicant.status === "Interview Incomplete")).length}
                onMarkAsOfferSent={() => setIsInternalOfferNoteDialogOpen(true)}
                onMarkAsReviewScheduled={() => {
                    setIsReviewScheduleManagerDialogOpen(true);
                }}
                onMarkAsPendingDecision={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected)
                            .map(applicant => applicant.applicant_id),
                        "pending_decision"
                    );
                }}
                onMarkAsHired={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected)
                            .map(applicant => applicant.applicant_id),
                        "hired"
                    );
                }}
                onMarkAsOfferResent={() => {
                    setIsMarkOfferResent(true);
                    setIsInternalOfferNoteDialogOpen(true);
                }}
                onMarkAsOfferDeclined={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected)
                            .map(applicant => applicant.applicant_id),
                        "offer_declined"
                    );
                }}
                onMarkAsArchived={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected)
                            .map(applicant => applicant.applicant_id),
                        "archived"
                    );
                }}
                onDelete={() => {
                    handleRequestDeleteCandidate(applicants.filter(applicant => applicant.is_selected));
                }}
                isDeleting={isDeletingCandidates}
                onMarkAsShortlisted={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id),
                        "shortlisted"
                    );
                }}
                onMarkAsOnHold={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id),
                        "on_hold"
                    );
                }}
                onMarkAsNotSuitable={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id),
                        "not_suitable"
                    );
                }}
                onMarkAsMoved={() => {
                    if (applicants.filter(applicant => applicant.is_selected && convertToLowerSnakeCase(applicant.status) !== "archived").length > 0) {
                        setIsRestoreValidationOpen(true);
                    } else {
                        onMarkApplicantStatus(
                            applicants.filter(applicant => applicant.is_selected && convertToLowerSnakeCase(applicant.status) === "archived")
                                .map(applicant => applicant.applicant_id),
                            "moved"
                        );
                    }
                }}
            />

            <AssessmentWarningDialog
                isOpen={isAssessmentWarningOpen}
                onClose={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected && applicant.status === "Assessment Completed")
                            .map(applicant => applicant.applicant_id),
                        "pending_review"
                    );
                    setIsAssessmentWarningOpen(false)
                }}
                ineligibleCount={countIneligibleCandidates}
            />

            <InterviewInvitationDialogV2
                showInterviewRoleSelector
                isOpen={showInterviewInvitationDialog}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                job={job}
                onClose={setShowInterviewInvitationDialog}
                onSendInvitation={handleSendInterviewInvitation}
            />

            <RejectCandidateDialogV2
                isOpen={showRejectOfferDialog}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                job={job}
                onClose={setShowRejectOfferDialog}
                onSendRejection={handleRejectCandidate}
            />

            {/* #3186/#3877: WhatsApp reject for flagged candidates with no email. */}
            <RejectCandidateWhatsappDialog
                isOpen={showWaRejectDialog}
                applicant={selectedApplicant}
                applicants={applicants.filter((a) => a.is_selected)}
                job={job}
                onClose={() => setShowWaRejectDialog(false)}
                onRejected={() => {
                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                    onLoadApplicationCount(job?.id || "");
                    refreshScreeningCounts();
                }}
            />

            {/* #3478: bulk screening invitation on WhatsApp. The BE (#4533) writes the
                Pending Assessment transition and the activity log itself, so — unlike the
                email path — there is no applicantUpdateStatus call here. */}
            <WhatsappScreeningInvitationDialog
                isOpen={showWaInvitationDialog}
                jobId={job?.id || ""}
                applicants={applicants.filter((a) => a.is_selected)}
                onClose={() => setShowWaInvitationDialog(false)}
                onSent={(result) => {
                    const sentCount = result?.sent?.length || 0;
                    const failedCount = result?.failed?.length || 0;
                    if (sentCount === 0) {
                        snackbar.showError("Screening invitation could not be sent. Please try again.");
                    } else if (failedCount > 0) {
                        snackbar.showSuccess(
                            `Screening invitation sent to ${sentCount} of ${sentCount + failedCount} candidates.`,
                            `${failedCount} could not be messaged on WhatsApp.`,
                        );
                    } else {
                        snackbar.showSuccess(
                            `Screening invitation sent to ${sentCount} candidate${sentCount === 1 ? "" : "s"}.`,
                        );
                    }
                    if (sentCount > 0) {
                        onClearSelection();
                        onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value || "");
                        onLoadApplicationCount(job?.id || "");
                        refreshScreeningCounts();
                    }
                }}
            />

            <JobOfferDialogV2
                isOpen={showJobOfferDialog}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                job={job}
                onClose={setShowJobOfferDialog}
                onSendJobOffer={handleSendJobOffer}
            />

            <AssessmentInvitationDialogV2
                type="send-invitation"
                isOpen={showAssessmentInvitationDialog}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                job={job}
                onClose={setShowAssessmentInvitationDialog}
                onSendInvitation={(body: SendCustomizedEmailRequest) => handleSendAssessmentInvitation(body, "send-invitation")}
            />

            <AssessmentInvitationDialogV2
                type="resend-invitation"
                isOpen={showAssessmentResendDialog}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                job={job}
                onClose={setShowAssessmentResendDialog}
                onSendInvitation={(body: SendCustomizedEmailRequest) => handleSendAssessmentInvitation(body, "resend-invitation")}
            />

            <AssessmentInvitationDialogV2
                type="reminder"
                isOpen={showAssessmentInvitationReminderDialog}
                applicants={applicants.filter(applicant => applicant.is_selected && applicant.status !== "Link Expired")}
                job={job}
                onClose={setShowAssessmentInvitationReminderDialog}
                onSendInvitation={(body: SendCustomizedEmailRequest) => handleSendAssessmentInvitation(body, "reminder")}
            />

            <AssessmentInvitationDialogV2
                type="retake"
                isOpen={showAssessmentRetakeDialog}
                applicants={applicants.filter(applicant => applicant.is_selected && applicant.status !== "Link Expired")}
                job={job}
                onClose={setShowAssessmentRetakeDialog}
                onSendInvitation={(body: SendCustomizedEmailRequest) => handleSendAssessmentInvitation(body, "retake")}
            />

            <ReviewScheduleManagerDialog
                isOpen={isReviewScheduleManagerDialogOpen}
                job={job}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                onClose={() => setIsReviewScheduleManagerDialogOpen(false)}
                onReload={() => {
                    onSearch(currentPage, searchQuery, getFunnelStatus(activeFunnelStage), matchFilter.value);
                    onLoadApplicationCount(job?.id || "");
                }}
            />

            <InternalOfferNoteDialog
                isOpen={isInternalOfferNoteDialogOpen}
                applicants={applicants.filter(applicant => applicant.is_selected)}
                onClose={() => setIsInternalOfferNoteDialogOpen(false)}
                onSubmit={(note: string) => {
                    handleMarkSendOffer(applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id), note);
                    setIsInternalOfferNoteDialogOpen(false);
                }}
            />

            {job && (
                <BulkCVUploadDialog
                    isOpen={isBulkCVUploadDialogOpen}
                    onClose={() => setIsBulkCVUploadDialogOpen(false)}
                    job={job}
                />
            )}


            <RestoreCandidatesValidationDialog
                isOpen={isRestoreValidationOpen}
                onClose={() => setIsRestoreValidationOpen(false)}
                ineligibleCount={applicants.filter(applicant => applicant.is_selected && convertToLowerSnakeCase(applicant.status) !== "archived").length}
                onContinue={() => {
                    onMarkApplicantStatus(
                        applicants.filter(applicant => applicant.is_selected && convertToLowerSnakeCase(applicant.status) === "archived")
                            .map(applicant => applicant.applicant_id),
                        "moved"
                    );
                }}
            />

            <DeleteCandidateDialog
                isOpen={isDeleteDialogOpen}
                isLoading={isDeletingCandidates}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setDeleteCandidateTargets([]);
                }}
                onConfirm={() => handleConfirmDeleteCandidates()}
                candidateNames={deleteCandidateTargets.map((a) => a.applicant_name || a.cv_data?.CV_data?.name || "—")}
                jobTitle={job?.job_title || ""}
            />
        </div>
    );
}
